import React, { useState, useEffect } from 'react'

import i18n from '@platform/i18n/config'

import { UI_TIMINGS } from '@/shared/utils/constants'
import { logger } from '@/shared/utils/logger'

interface I18nWrapperProps {
  children: React.ReactNode
  i18nReady: boolean
  /**
   * Hard cap on the "waiting for translations" spinner. i18next resources come
   * from the backend, so an unreachable backend must NEVER hold the UI hostage:
   * once this elapses the children render regardless (degraded but usable).
   */
  maxWaitMs?: number
}

/** Default hard cap before the app renders without confirmed translations. */
const DEFAULT_MAX_WAIT_MS = 2000

/**
 * Loading component for i18n initialization
 */
const I18nLoadingSpinner: React.FC = () => {
  // Use i18n directly since this loads before translations might be ready
  const loadingText = i18n.isInitialized
    ? i18n.t('common:i18nWrapper.loadingTranslations')
    : 'Loading translations...'

  return (
    <div className="min-h-screen bg-crypto-dark flex items-center justify-center">
      <div className="flex flex-col items-center space-y-4">
        <div className="w-8 h-8 border-4 border-crypto-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-crypto-text text-sm">{loadingText}</p>
      </div>
    </div>
  )
}

/**
 * I18n Wrapper Component
 * Ensures translations are fully loaded before rendering children
 * Simplified to avoid hook call issues
 */
export const I18nWrapper: React.FC<I18nWrapperProps> = ({
  children,
  i18nReady: initialReady,
  maxWaitMs = DEFAULT_MAX_WAIT_MS,
}) => {
  const [translationsReady, setTranslationsReady] = useState(initialReady)

  // Hard cap: render the app even if i18next never finishes initializing.
  useEffect(() => {
    if (initialReady) return
    const giveUpTimer = setTimeout(() => {
      logger.warn('[I18nWrapper] Translations still not ready — rendering the app anyway')
      setTranslationsReady(true)
    }, maxWaitMs)
    return () => clearTimeout(giveUpTimer)
  }, [initialReady, maxWaitMs])

  useEffect(() => {
    if (initialReady) {
      logger.info('[I18nWrapper] i18n already ready from props')
      setTranslationsReady(true)
      return
    }

    logger.info('[I18nWrapper] Checking i18n initialization status...')
    logger.info('[I18nWrapper] i18n.isInitialized:', i18n.isInitialized)

    // Cancellation guard: without it the poll below keeps rescheduling itself
    // forever when i18next never initializes (dead backend).
    let isCancelled = false
    let pollTimer: ReturnType<typeof setTimeout> | undefined

    // Check if translations are actually available
    const checkTranslations = () => {
      if (isCancelled) return
      const isInitialized = i18n.isInitialized
      const hasResources = i18n.hasResourceBundle?.(i18n.language, 'common')

      logger.info('[I18nWrapper] isInitialized:', isInitialized)
      logger.info('[I18nWrapper] hasResources:', hasResources)

      if (isInitialized) {
        // Test translation to ensure it works
        try {
          const testTranslation = i18n.t('landing.title')
          logger.info('[I18nWrapper] Test translation result:', testTranslation)

          // Only set ready if we get actual translated text, not the key
          if (testTranslation && testTranslation !== 'landing.title') {
            logger.info('[I18nWrapper] Translations are working properly')
            setTranslationsReady(true)
            return
          }
        } catch (error) {
          logger.warn('[I18nWrapper] Translation test failed:', error)
        }

        // If we have an initialized i18n but no proper translations, still continue
        // This prevents infinite loading when translations might not be available
        logger.info('[I18nWrapper] i18n initialized, proceeding without full translations')
        setTranslationsReady(true)
      } else {
        logger.info('[I18nWrapper] i18n not ready yet, retrying...')
        pollTimer = setTimeout(checkTranslations, UI_TIMINGS.I18N_RETRY)
      }
    }

    // Start checking translations
    checkTranslations()

    return () => {
      isCancelled = true
      if (pollTimer !== undefined) clearTimeout(pollTimer)
    }
  }, [initialReady])

  // Show loading spinner while translations are not ready
  if (!translationsReady) {
    logger.info('[I18nWrapper] Showing loading spinner')
    return <I18nLoadingSpinner />
  }

  logger.info('[I18nWrapper] Rendering children with translations ready')
  return <>{children}</>
}
